import React from "react";
import { Box, Typography } from "@material-ui/core";
import { CustomBadge, CustomCard } from "../../../../Components";
import { useSelectedTheme, useTranslate } from "../../../../Hooks";

import useStyles from "./styles";

import {
  UnitSizeIcon,
  BedIcon,
  BathIcon,
  CheckIcon,
  CloseXIcon,
} from "../../../../assets/icons";

function RotationCriteriaSummary({ generateStepperActions, schemaValues }) {
  const styles = useStyles();
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const {
    isOverrideLeadCapacitySettingEnabled,
    rotationSchemeLeadDistributionType,
    label,
    sla,
    rotationSchemeMedias,
    rotationSchemeBranches,
    order,
    buyer,
    tenant,
    vip,
    normal,
    vvip,
    isBulk,
    rotationSchemaReferredBys,
    developer,
    priceRange,
    size,
    bedrooms,
    bathrooms,
    propertyCampaignIds,
    rotationPreferredLanguages,
    rotationSchemeCities,
    rotationSchemaMethodOfContacts,
    rotationSchemeUnitTypes,
    rotationSchemeCommunities,
    rotationSchemeSubCommunities,
    rotationSchemeProperties,
    rotationSchemeDistricts,
    rotationSchemeCountries,
  } = schemaValues;

  const noLocationData =
    rotationSchemeCountries.length === 0 &&
    rotationSchemeCities.length === 0 &&
    (rotationSchemeCommunities.length === 0) &
      (rotationSchemeSubCommunities.length === 0);

  const agentLimitRule = {
    false: {
      value: "false",
      label: translate("agent-load-all-system"),
    },
    true: {
      value: "true",
      label: translate("agent-load-current-schema"),
    },
  };

  const distributionStrategy = {
    3: {
      value: "3",
      label: translate("dist-strategy-all-system"),
    },
    1: {
      value: "1",
      label: translate("dist-strategy-current-schema-assigned"),
    },
    2: {
      value: "2",
      label: translate("dist-strategy-current-schema-active"),
    },
    4: {
      value: "4",
      label: translate("dist-strategy-one-by-one"),
    },
  };

  return (
    <div>
      <Box
        marginTop={"32px"}
        marginBottom={"24px"}
        className={styles.stepperActionContainer}
      >
        <div>
          <Typography
            className={styles.secondaryTitle}
            variant="h6"
            component="h2"
          >
            {translate("rotation-summary")}
          </Typography>
          <Typography component={"p"} className={styles.subText}>
            {translate("rotation-summary-subtext")}
          </Typography>
        </div>
        <Box
          display="flex"
          flexDirection="row-reverse"
          flexBasis={"40%"}
          style={{ gap: "12px", maxHeight: "40px" }}
        >
          {generateStepperActions()}
        </Box>
      </Box>
      <CustomCard
        boxShadow="xs"
        borderRadius="xl"
        borderColor="secondary"
        classes={styles.summaryCard}
      >
        <Typography
          className={styles.secondaryTitle}
          variant="h6"
          component="h3"
        >
          {label}
        </Typography>
        <Box className={styles.summaryContainer}>
          <Box className={styles.summaryContent}>
            <div>
              <Typography className={styles.title} component={"p"}>
                {translate("referred-by-label")}
              </Typography>
              {rotationSchemaReferredBys.map(({ fullName, userId }) => (
                <Typography
                  key={userId}
                  className={styles.referalName}
                  component={"p"}
                >
                  {fullName ?? "-"}
                </Typography>
              ))}
            </div>
            <CustomCard
              boxShadow="xs"
              borderRadius="xl"
              borderColor="secondary"
              classes={styles.propertyContainer}
            >
              <Typography className={styles.title} component={"p"}>
                {`${rotationSchemeDistricts.map(
                  ({ districtName }) => `${districtName}, `
                )} ${rotationSchemeProperties.map(
                  ({ propertyName }) => `${propertyName}, `
                )} `}
              </Typography>
              <Typography className={styles.title} component={"p"}>
                {`${priceRange[0]} - ${priceRange[1]} AED`}
              </Typography>
              <Typography className={styles.title} component={"p"}>
                <Box className={styles.unitDetailsContainer}>
                  <div className={styles.propertyInfo}>
                    <UnitSizeIcon
                      className={styles.unitsIcon}
                      width="20"
                      height="20"
                    />
                    <span>{`${size[0]} - ${size[1]} ft`}</span>
                  </div>
                  <div className={styles.propertyInfo}>
                    <BedIcon
                      className={styles.unitsIcon}
                      width="20"
                      height="20"
                    />
                    <span>{`${bedrooms[0]} - ${bedrooms[1]}`}</span>
                  </div>
                  <div className={styles.propertyInfo}>
                    <BathIcon
                      className={styles.unitsIcon}
                      width="20"
                      height="20"
                    />
                    <span>{`${bathrooms[0]} - ${bathrooms[1]}`}</span>
                  </div>
                </Box>
              </Typography>
            </CustomCard>
          </Box>
          <Box className={styles.contentWrapper}>
            <Box className={styles.summaryContent}>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("schema-order-label")}
                </Typography>
                <Typography className={styles.value} component={"p"}>
                  {order}
                </Typography>
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  Allow bulk leads
                </Typography>
                <Box className={styles.isBulk}>
                  {isBulk ? (
                    <CheckIcon
                      className={styles.checkIcon}
                      width="20"
                      height="20"
                    />
                  ) : (
                    <CloseXIcon
                      width="20"
                      height="20"
                      className={styles.CloseXIcon}
                    />
                  )}
                  <Typography className={styles.value} component={"p"}>
                    {isBulk ? translate("yes") : translate("no")}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("unit-type-label")}
                </Typography>
                {rotationSchemeUnitTypes.length === 0 && (
                  <Typography className={styles.value} component={"p"}>
                    -
                  </Typography>
                )}
                {rotationSchemeUnitTypes.map(({ unitTypeName, unitTypeId }) => (
                  <CustomBadge
                    key={unitTypeId}
                    label={unitTypeName}
                    SizeVariant={"large"}
                    BackgroundColor={palette.background?.paper}
                    BorderColor={palette.border?.primary}
                    Color={palette.utility.utility_gray_700}
                  />
                ))}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("specific-location-label")}
                </Typography>
                <Box>
                  {noLocationData && (
                    <Typography className={styles.value} component={"p"}>
                      -
                    </Typography>
                  )}
                  {rotationSchemeCountries.map(({ countryName, countryId }) => (
                    <CustomBadge
                      label={countryName}
                      key={countryId}
                      SizeVariant={"large"}
                      BackgroundColor={palette.background?.paper}
                      BorderColor={palette.border?.primary}
                      Color={palette.utility.utility_gray_700}
                    />
                  ))}
                  {rotationSchemeCities.map(({ cityName, cityId }) => (
                    <CustomBadge
                      label={cityName}
                      key={cityId}
                      SizeVariant={"large"}
                      BackgroundColor={palette.background?.paper}
                      BorderColor={palette.border?.primary}
                      Color={palette.utility.utility_gray_700}
                    />
                  ))}
                  {rotationSchemeCommunities.map(
                    ({ communityName, communityId }) => (
                      <CustomBadge
                        label={communityName}
                        key={communityId}
                        SizeVariant={"large"}
                        BackgroundColor={palette.background?.paper}
                        BorderColor={palette.border?.primary}
                        Color={palette.utility.utility_gray_700}
                      />
                    )
                  )}
                  {rotationSchemeSubCommunities.map(
                    ({ subCommunityName, subCommunityId }) => (
                      <CustomBadge
                        label={subCommunityName}
                        key={subCommunityId}
                        SizeVariant={"large"}
                        BackgroundColor={palette.background?.paper}
                        BorderColor={palette.border?.primary}
                        Color={palette.utility.utility_gray_700}
                      />
                    )
                  )}
                </Box>
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("lead-distribution-strategy")}
                </Typography>
                <Typography className={styles.value} component={"p"}>
                  {
                    distributionStrategy[rotationSchemeLeadDistributionType]
                      .label
                  }
                </Typography>
              </Box>
            </Box>
            <Box className={styles.summaryContent}>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("lead-type-label")}
                </Typography>
                {buyer && (
                  <Typography className={styles.value} component={"p"}>
                    {translate("buyer")}
                  </Typography>
                )}
                {tenant && (
                  <Typography className={styles.value} component={"p"}>
                    {translate("tenant")}
                  </Typography>
                )}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("media-detail-label")}
                </Typography>
                {rotationSchemeMedias.length === 0 && (
                  <Typography className={styles.value} component={"p"}>
                    -
                  </Typography>
                )}
                {rotationSchemeMedias.map(
                  ({ mediaDetailsName, mediaDetailsId }) => (
                    <CustomBadge
                      label={mediaDetailsName}
                      key={mediaDetailsId}
                      SizeVariant={"large"}
                      BackgroundColor={palette.background?.paper}
                      BorderColor={palette.border?.primary}
                      Color={palette.utility.utility_gray_700}
                    />
                  )
                )}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("method-of-contacts-label")}
                </Typography>
                {rotationSchemaMethodOfContacts.length === 0 && (
                  <Typography className={styles.value} component={"p"}>
                    -
                  </Typography>
                )}
                {rotationSchemaMethodOfContacts.map(
                  ({ lookupItemId, lookupItemName }) => (
                    <CustomBadge
                      key={lookupItemId}
                      label={lookupItemName}
                      SizeVariant={"large"}
                      BackgroundColor={palette.background?.paper}
                      BorderColor={palette.border?.primary}
                      Color={palette.utility.utility_gray_700}
                    />
                  )
                )}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("developer-label")}
                </Typography>
                {developer.length === 0 && (
                  <Typography className={styles.value} component={"p"}>
                    -
                  </Typography>
                )}
                {developer.map(({ developerName, developerId }) => (
                  <CustomBadge
                    key={developerId}
                    label={developerName}
                    SizeVariant={"large"}
                    BackgroundColor={palette.background?.paper}
                    BorderColor={palette.border?.primary}
                    Color={palette.utility.utility_gray_700}
                  />
                ))}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("agent-load-limit-rule")}
                </Typography>
                <Typography className={styles.value} component={"p"}>
                  {agentLimitRule[isOverrideLeadCapacitySettingEnabled].label}
                </Typography>
              </Box>
            </Box>
            <Box className={styles.summaryContent}>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  Lead Class{" "}
                </Typography>
                {normal && (
                  <Typography className={styles.value} component={"p"}>
                    {translate("normal")}
                  </Typography>
                )}
                {vip && (
                  <Typography className={styles.value} component={"p"}>
                    {translate("vip")}
                  </Typography>
                )}
                {vvip && (
                  <Typography className={styles.value} component={"p"}>
                    {translate("vvip")}
                  </Typography>
                )}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("lead-campaign-label")}
                </Typography>
                {propertyCampaignIds.length === 0 && (
                  <Typography className={styles.value} component={"p"}>
                    -
                  </Typography>
                )}
                {propertyCampaignIds.map(({ propertyCampaignId, name }) => (
                  <CustomBadge
                    label={name}
                    key={propertyCampaignId}
                    SizeVariant={"large"}
                    BackgroundColor={palette.background?.paper}
                    BorderColor={palette.border?.primary}
                    Color={palette.utility.utility_gray_700}
                  />
                ))}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("lead-preferred-language-label")}
                </Typography>
                {rotationPreferredLanguages.length === 0 && (
                  <Typography className={styles.value} component={"p"}>
                    -
                  </Typography>
                )}
                {rotationPreferredLanguages.map(
                  ({ lookupItemName, languageId }) => (
                    <CustomBadge
                      key={languageId}
                      label={lookupItemName}
                      SizeVariant={"large"}
                      BackgroundColor={palette.background?.paper}
                      BorderColor={palette.border?.primary}
                      Color={palette.utility.utility_gray_700}
                    />
                  )
                )}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("branch-name")}
                </Typography>
                {rotationSchemeBranches.length === 0 && (
                  <Typography className={styles.value} component={"p"}>
                    -
                  </Typography>
                )}
                {rotationSchemeBranches.map(({ branchId, branchName }) => (
                  <CustomBadge
                    label={branchName}
                    key={branchId}
                    SizeVariant={"large"}
                    BackgroundColor={palette.background?.paper}
                    BorderColor={palette.border?.primary}
                    Color={palette.utility.utility_gray_700}
                  />
                ))}
              </Box>
              <Box className={styles.schemaProperty}>
                <Typography className={styles.title} component={"p"}>
                  {translate("sla")}
                </Typography>
                <Typography className={styles.value} component={"p"}>
                  {sla ?? "-"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CustomCard>
    </div>
  );
}

export default RotationCriteriaSummary;
